/**
 * Base API Client for unified service architecture
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { config } from '../../config';

/** Axios request config plus statuses the caller treats as a normal answer (no error log). */
export interface ExpectingRequestConfig extends AxiosRequestConfig {
  expectedStatuses?: number[];
}

export interface ApiConfig {
  baseURL?: string;
  timeout?: number;
  withCredentials?: boolean;
  headers?: Record<string, string>;
}

export interface ApiErrorResponse {
  code: string;
  message: string;
  details?: Record<string, any>;
  timestamp: number;
}

export class ApiError extends Error {
  public code: string;
  public status: number;
  public details?: Record<string, any>;

  constructor(message: string, code: string, status: number, details?: Record<string, any>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export abstract class BaseApiClient {
  protected client: AxiosInstance;
  protected baseURL: string;

  constructor(apiConfig: ApiConfig = {}) {
    this.baseURL = apiConfig.baseURL || `${config.apiBaseUrl}/api`;
    
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: apiConfig.timeout || 30000,
      withCredentials: apiConfig.withCredentials || false,
      headers: {
        'Content-Type': 'application/json',
        ...apiConfig.headers,
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add tenant ID if available
        const tenantId = this.getTenantId();
        if (tenantId) {
          config.headers['X-Tenant-ID'] = tenantId;
        }

        return config;
      },
      (error) => {
        console.error('[API] Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        const apiError = this.handleApiError(error);
        // Callers that expect a status (e.g. 404 = "no station linked to this trafo") pass
        // expectedStatuses; those answers are not errors and must not spam the console.
        const expected = (error.config as ExpectingRequestConfig | undefined)?.expectedStatuses;
        if (!expected || !expected.includes(apiError.status)) {
          console.error('[API] Response error:', apiError);
        }
        return Promise.reject(apiError);
      }
    );
  }

  private handleApiError(error: AxiosError): ApiError {
    const response = error.response;
    const status = response?.status || 0;
    const data = response?.data as any;

    let message = 'An unknown error occurred';
    let code = 'UNKNOWN_ERROR';
    let details: Record<string, any> | undefined;

    if (data?.message) {
      message = data.message;
    } else if (error.message) {
      message = error.message;
    }

    if (data?.code) {
      code = data.code;
    } else if (status) {
      code = `HTTP_${status}`;
    }

    if (data?.details) {
      details = data.details;
    }

    // Handle specific HTTP status codes
    switch (status) {
      case 401:
        code = 'UNAUTHORIZED';
        message = 'Authentication required';
        // Auto-logout on 401: clear invalid token and reload to show login page
        this.handleUnauthorized();
        break;
      case 403:
        code = 'FORBIDDEN';
        message = 'Access denied';
        break;
      case 404:
        code = 'NOT_FOUND';
        message = 'Resource not found';
        break;
      case 422:
        code = 'VALIDATION_ERROR';
        message = 'Validation failed';
        break;
      case 429:
        code = 'RATE_LIMIT_EXCEEDED';
        message = 'Too many requests';
        break;
      case 500:
        code = 'INTERNAL_SERVER_ERROR';
        message = 'Internal server error';
        break;
      case 503:
        code = 'SERVICE_UNAVAILABLE';
        message = 'Service temporarily unavailable';
        break;
    }

    return new ApiError(message, code, status, details);
  }

  /**
   * Handle 401 Unauthorized responses by clearing auth and redirecting to login
   */
  private handleUnauthorized(): void {
    if (typeof window === 'undefined') return;

    // Only trigger reload if there was actually a token (user was logged in)
    // This prevents reload loops when user is not yet authenticated
    const hadToken = localStorage.getItem('auth_token');
    if (!hadToken) {
      return;
    }

    // Prevent multiple reloads
    const isReloading = sessionStorage.getItem('auth_reloading');
    if (isReloading) return;


    // Clear all auth tokens
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('auth_token_expiry');
    localStorage.removeItem('auth_tenant');

    // Mark as reloading to prevent loop
    sessionStorage.setItem('auth_reloading', 'true');

    // Reload page to show login
    window.location.reload();
  }

  protected getAuthToken(): string | null {
    // Override in subclasses or implement token management
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
  }

  protected getTenantId(): string | null {
    // Override in subclasses or implement tenant management
    if (typeof window === 'undefined') return config.actingTenant;
    return localStorage.getItem('auth_tenant') || config.actingTenant;
  }

  // HTTP method wrappers
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  // Utility methods
  protected buildUrl(endpoint: string, params?: Record<string, any>): string {
    let url = endpoint;
    
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    
    return url;
  }

  protected async retry<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<T> {
    let lastError: Error = new Error('No attempts made');
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        
        // Don't retry for client errors (4xx) except 429 (rate limit)
        if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 429) {
          throw error;
        }
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
      }
    }
    
    throw lastError;
  }

  // Health check
  public async healthCheck(): Promise<{ status: string; timestamp: number }> {
    return this.get('/health');
  }

  // Update client configuration
  public updateConfig(config: Partial<ApiConfig>): void {
    if (config.baseURL) {
      this.client.defaults.baseURL = config.baseURL;
    }
    if (config.timeout) {
      this.client.defaults.timeout = config.timeout;
    }
    if (config.headers) {
      Object.assign(this.client.defaults.headers.common, config.headers);
    }
  }
}